import { element } from "./dom.js";
import { record, strings } from "./sheet-data.js";
import {
  type FlowPart,
  type FlowRow,
  SheetFlow,
  sheetCue,
  sheetHanzi,
  sheetNumber,
  sheetRows,
} from "./sheet-flow.js";
import type { GlyphLibrary } from "./worksheet-drawing.js";
import type { SheetLabels } from "./worksheet-page.js";
import { sized } from "./worksheet-page.js";
import type { PartState } from "./part-state.js";

/**
 * The look-alike characters sheet 形近字, as pages ready to print.
 *
 * Two parts and the answers. **Choose the character** sets a pair to a row,
 * a sentence for each of its characters side by side, each with the pair to
 * circle in its gap. **Which is which** gives each pair's readings and
 * meanings over a square for each. The answers mark each one, then give the
 * 形近字 list's note on telling each pair apart.
 *
 * What each character is asked with is worked out at build time, in
 * `src/content/worksheets/lookalike-data.ts`, and fetched a character at a
 * time.
 */

/** The parts a look-alike sheet prints. */
export type LookalikePart = "choose" | "which" | "key";

/** A sentence with its gap, as the data file holds it. */
export type LookalikeGap = readonly [
  before: string,
  after: string,
  pinyin: string,
  english: string,
];

/** One character, as the data file holds it. */
export interface LookalikeCharacter {
  readonly reading: string;
  readonly cue: string;
  readonly partners: readonly string[];
  readonly sentences: readonly LookalikeGap[];
}

/** The data file. */
export interface LookalikeFile {
  readonly characters: ReadonlyMap<string, LookalikeCharacter>;
  readonly notes: ReadonlyMap<string, string>;
}

function gap(value: unknown): LookalikeGap | undefined {
  const parts = strings(value);

  return parts?.length === 4
    ? [parts[0]!, parts[1]!, parts[2]!, parts[3]!]
    : undefined;
}

/** A character read defensively, or nothing for one that is not. */
function readCharacter(value: unknown): LookalikeCharacter | undefined {
  const entry = record(value);
  const partners = strings(entry?.["partners"]);
  const sentences = Array.isArray(entry?.["sentences"])
    ? (entry["sentences"] as unknown[]).flatMap((one) => {
        const read = gap(one);

        return read === undefined ? [] : [read];
      })
    : [];

  return typeof entry?.["reading"] === "string" &&
    typeof entry["cue"] === "string" &&
    partners !== undefined
    ? { reading: entry["reading"], cue: entry["cue"], partners, sentences }
    : undefined;
}

/** An object's string values, keeping no other kind. */
function stringValues(
  values: Readonly<Record<string, unknown>>,
): readonly (readonly [string, string])[] {
  return Object.entries(values).filter(
    (pair): pair is [string, string] => typeof pair[1] === "string",
  );
}

/** The whole data file read defensively, or nothing for a body that is not one. */
export function lookalikeFile(body: unknown): LookalikeFile | undefined {
  const file = record(body);
  const characters = record(file?.["characters"]);
  const notes = record(file?.["notes"]);
  if (characters === undefined || notes === undefined) {
    return undefined;
  }
  const read = new Map<string, LookalikeCharacter>();
  for (const [character, value] of Object.entries(characters)) {
    const entry = readCharacter(value);
    if (entry !== undefined) {
      read.set(character, entry);
    }
  }

  return { characters: read, notes: new Map(stringValues(notes)) };
}

/**
 * Which characters the sheet can ask about, each with the characters it is
 * paired with, published at `keys.json`. These are the 形近字 list's pairs,
 * which is all `lookalikePairs` needs to choose.
 */
export type LookalikeKeys = ReadonlyMap<string, readonly string[]>;

/** The keys read defensively, or nothing for a body that is not them. */
export function lookalikeKeys(body: unknown): LookalikeKeys | undefined {
  const partners = record(record(body)?.["partners"]);
  if (partners === undefined) {
    return undefined;
  }
  const keys = new Map<string, readonly string[]>();
  for (const [character, value] of Object.entries(partners)) {
    const listed = strings(value);
    if (listed !== undefined) {
      keys.set(character, listed);
    }
  }

  return keys;
}

/**
 * One character, published at `data/<key>.json`, with the notes on its pairs
 * keyed by its partner.
 */
export interface LookalikeShard {
  readonly character: LookalikeCharacter;
  readonly notes: ReadonlyMap<string, string>;
}

/** An item read defensively, or nothing for a body that is not one. */
export function lookalikeShard(body: unknown): LookalikeShard | undefined {
  const character = readCharacter(body);
  const notes = record(record(body)?.["notes"]);

  return character === undefined
    ? undefined
    : {
        character,
        notes: new Map(notes === undefined ? [] : stringValues(notes)),
      };
}

/** A character known only by its partners, until its item is fetched. */
function unfetched(partners: readonly string[]): LookalikeCharacter {
  return { reading: "", cue: "", partners, sentences: [] };
}

/**
 * The data file made of the keys and the items fetched, each character not
 * fetched standing in by its partners alone.
 */
export function lookalikeFileOf(
  keys: LookalikeKeys,
  items: ReadonlyMap<string, LookalikeShard> = new Map(),
): LookalikeFile {
  const characters = new Map<string, LookalikeCharacter>();
  for (const [character, partners] of keys) {
    characters.set(
      character,
      items.get(character)?.character ?? unfetched(partners),
    );
  }
  const notes = new Map<string, string>();
  for (const [character, item] of items) {
    for (const [partner, note] of item.notes) {
      notes.set(`${character}${partner}`, note);
    }
  }

  return { characters, notes };
}

/**
 * The items the pairs for these characters need, worked out from the keys
 * alone: both characters of each pair `lookalikePairs` would choose. It
 * chooses by the partners, which the keys hold, so the sheet drawn from
 * these items is the one the whole file would draw.
 * @param characters - The sheet's characters, in the order they were given.
 * @param limit - The most pairs a sheet takes.
 */
export function lookalikeNeeds(
  keys: LookalikeKeys,
  characters: readonly string[],
  limit: number,
): readonly string[] {
  return lookalikePairs(lookalikeFileOf(keys), characters, limit).pairs.flatMap(
    (pair) => [pair.one, pair.other],
  );
}

/** One pair on the sheet. */
export interface LookalikePair {
  readonly one: string;
  readonly other: string;
  readonly oneEntry: LookalikeCharacter;
  readonly otherEntry: LookalikeCharacter;
  readonly note: string | undefined;
}

/** The pairs the sheet asks about, and the characters it could not pair. */
export interface LookalikeChoice {
  readonly pairs: readonly LookalikePair[];
  readonly unpaired: readonly string[];
}

/**
 * The pairs for these characters, each character in one pair at most.
 *
 * A character is paired with a partner the sheet was also given where it has
 * one, so a List holding 已 and 己 asks about them together, and with its
 * first partner otherwise.
 * @param characters - The sheet's characters, in the order they were given.
 * @param limit - The most pairs a sheet takes.
 */
export function lookalikePairs(
  file: LookalikeFile,
  characters: readonly string[],
  limit: number,
): LookalikeChoice {
  const given = new Set(characters);
  const used = new Set<string>();
  const pairs: LookalikePair[] = [];
  const unpaired: string[] = [];

  for (const one of characters) {
    if (used.has(one)) {
      continue;
    }
    const oneEntry = file.characters.get(one);
    const partners = (oneEntry?.partners ?? []).filter(
      (partner) => !used.has(partner) && file.characters.has(partner),
    );
    const other = partners.find((partner) => given.has(partner)) ?? partners[0];
    if (oneEntry === undefined || other === undefined) {
      unpaired.push(one);
      continue;
    }
    if (pairs.length >= limit) {
      break;
    }
    used.add(one);
    used.add(other);
    pairs.push({
      one,
      other,
      oneEntry,
      otherEntry: file.characters.get(other)!,
      note:
        file.notes.get(`${one}${other}`) ?? file.notes.get(`${other}${one}`),
    });
  }

  return { pairs, unpaired };
}

/** Each part's rows, in millimetres. */
export const lookalikeHeights = {
  choose: 16,
  which: 26,
  key: 6,
} as const;

/**
 * The least each part's rows can be drawn at, on a page drawn closer to take
 * the last of its part. Measured in the browser, where a sentence row holds
 * 14.3 mm, a "which is which" row 24.5 mm and an answer 5 mm. A note row's
 * height is estimated from its length, and it can give up `noteSpare`.
 */
export const lookalikeLeast = { choose: 15, which: 25, key: 5.5 } as const;

/**
 * How much of a note row's estimated height it can give up. The estimate
 * allows for a line more than most notes take. One estimated at 18.6 mm held
 * 11.5 mm in the browser.
 */
const noteSpare = 2;

/** The square a character is written in, on "which is which". */
const whichSquare = 13;

/**
 * The order a pair is offered in a gap, worked out from the item's number so
 * that the answer is not always first and a sheet prints the same every time.
 */
function offered(
  number: number,
  answer: string,
  other: string,
): readonly [string, string] {
  return number % 3 === 0 || number % 4 === 1
    ? [answer, other]
    : [other, answer];
}

/** One sentence's question, numbered. */
interface Question {
  readonly number: number;
  readonly answer: string;
  readonly other: string;
  readonly gap: LookalikeGap;
}

/** What drawing the sheet needs. */
export interface LookalikeSheetProperties {
  readonly library: GlyphLibrary;
}

/** Draws look-alike sheets. */
export class LookalikeSheet {
  readonly #library: GlyphLibrary;

  constructor(properties: LookalikeSheetProperties) {
    this.#library = properties.library;
  }

  /** Every page of the sheet, leaving out the parts not asked for. */
  pages(
    state: PartState<LookalikePart>,
    pairs: readonly LookalikePair[],
    labels: SheetLabels,
  ): readonly HTMLElement[] {
    let number = 0;
    const rows: Question[][] = [];
    // A sentence serves one gap on a sheet. 自 and 己 both have 自己, and
    // without this the same sentence asked about each.
    const used = new Set<string>();
    if (state.choose) {
      for (const pair of pairs) {
        const row: Question[] = [];
        for (const [answer, other, entry] of [
          [pair.one, pair.other, pair.oneEntry],
          [pair.other, pair.one, pair.otherEntry],
        ] as const) {
          const sentence = entry.sentences.find(
            (gap) => !used.has(`${gap[0]}${answer}${gap[1]}`),
          );
          if (sentence !== undefined) {
            used.add(`${sentence[0]}${answer}${sentence[1]}`);
            number += 1;
            row.push({ number, answer, other, gap: sentence });
          }
        }
        if (row.length > 0) {
          rows.push(row);
        }
      }
    }
    const whichNumbers = pairs.map(() => (state.which ? (number += 1) : 0));

    const parts: FlowPart[] = [];
    let part = 0;
    if (rows.length > 0) {
      part += 1;
      parts.push({
        number: part,
        title: "Choose the character",
        chinese: "选字填空",
        note: "Circle the character that completes each sentence. The pinyin shows how to read the sentence. The English gives its meaning.",
        rows: rows.map((row) => this.#chooseRow(row)),
      });
    }
    if (state.which && pairs.length > 0) {
      part += 1;
      parts.push({
        number: part,
        title: "Which is which",
        chinese: "辨字",
        note: "Each pair looks similar. Use each reading and meaning to identify a character from the pair. Write it in the square.",
        rows: sheetRows(
          pairs.map((pair, at) => this.#whichItem(pair, whichNumbers[at]!)),
          4,
          lookalikeHeights.which,
          "cb-wl-which",
          lookalikeLeast.which,
        ),
      });
    }
    if (state.key && parts.length > 0) {
      parts.push(
        ...this.#key(rows.flat(), pairs, state.which ? whichNumbers : []),
      );
    }

    return new SheetFlow({
      paper: state.paper,
      kind: "Look-alike characters",
      kindChinese: "形近字",
      parts,
      labels,
    }).pages();
  }

  #chooseRow(questions: readonly Question[]): FlowRow {
    const items = questions.map(({ number, answer, other, gap }) => {
      const [left, right] = offered(number, answer, other);
      const choice = element("span", {
        className: "cb-wl-choice",
        children: [
          element("span", { text: left }),
          element("span", { text: right }),
        ],
      });
      const sentence = element("p", {
        className: "cb-wl-sentence cb-wf-hz",
        attributes: { lang: "zh-Hans" },
        children: [
          document.createTextNode(gap[0]),
          choice,
          document.createTextNode(gap[1]),
        ],
      });

      return element("div", {
        className: "cb-wl-choose",
        children: [
          sheetNumber(number),
          sentence,
          element("span"),
          sheetCue(gap[2], gap[3]),
        ],
      });
    });

    return sheetRows(
      items,
      2,
      lookalikeHeights.choose,
      "cb-wl-pair",
      lookalikeLeast.choose,
    )[0]!;
  }

  #whichItem(pair: LookalikePair, number: number): HTMLElement {
    const side = (entry: LookalikeCharacter): HTMLElement =>
      element("div", {
        className: "cb-wl-side",
        children: [
          this.#library.row(
            whichSquare,
            [{ kind: "empty" }],
            undefined,
            "tracing",
          ),
          sheetCue(entry.reading, entry.cue),
        ],
      });
    const sides =
      number % 2 === 0
        ? [side(pair.otherEntry), side(pair.oneEntry)]
        : [side(pair.oneEntry), side(pair.otherEntry)];

    return element("div", {
      className: "cb-wl-which-pair",
      children: [sheetNumber(number), ...sides],
    });
  }

  #key(
    questions: readonly Question[],
    pairs: readonly LookalikePair[],
    whichNumbers: readonly number[],
  ): readonly FlowPart[] {
    const parts: FlowPart[] = [];
    let part = 0;
    if (questions.length > 0) {
      part += 1;
      parts.push({
        number: part,
        title: "Choose the character",
        rows: sheetRows(
          questions.map(({ number, answer, gap }) =>
            element("div", {
              className: "cb-wf-key-item",
              children: [
                sheetNumber(number),
                element("span", {
                  className: "cb-wf-hz",
                  attributes: { lang: "zh-Hans" },
                  children: [
                    document.createTextNode(gap[0]),
                    element("span", { className: "cb-wf-mark", text: answer }),
                    document.createTextNode(gap[1]),
                  ],
                }),
              ],
            }),
          ),
          3,
          lookalikeHeights.key,
          "",
          lookalikeLeast.key,
        ),
        startsPage: true,
      });
    }
    if (whichNumbers.length > 0) {
      part += 1;
      parts.push({
        number: part,
        title: "Which is which",
        rows: sheetRows(
          pairs.map((pair, at) => {
            const [first, second] =
              whichNumbers[at]! % 2 === 0
                ? [pair.other, pair.one]
                : [pair.one, pair.other];

            return element("div", {
              className: "cb-wf-key-item",
              children: [
                sheetNumber(whichNumbers[at]!),
                element("span", {
                  className: "cb-wf-hz cb-wf-mark",
                  text: `${first} ${second}`,
                  attributes: { lang: "zh-Hans" },
                }),
              ],
            });
          }),
          8,
          lookalikeHeights.key,
          "",
          lookalikeLeast.key,
        ),
        startsPage: parts.length === 0,
      });
    }
    const noted = pairs.filter((pair) => pair.note !== undefined);
    if (noted.length > 0) {
      parts.push({
        title: "Telling them apart",
        rows: this.#notes(noted),
        startsPage: parts.length === 0,
      });
    }

    return parts;
  }

  /** The notes, two to a row, each row as tall as its longer note. */
  #notes(pairs: readonly LookalikePair[]): readonly FlowRow[] {
    const rows: FlowRow[] = [];
    for (let at = 0; at < pairs.length; at += 2) {
      const pair = pairs.slice(at, at + 2);
      const height = Math.max(...pair.map((one) => noteHeight(one.note!)));
      const row = element("div", {
        className: "cb-wf-row cb-wl-notes",
        children: pair.map((one) =>
          element("div", {
            className: "cb-wl-note",
            children: [
              sheetHanzi(`${one.one}${one.other}`, "cb-wl-note-pair"),
              element("p", { text: one.note! }),
            ],
          }),
        ),
      });
      row.style.setProperty("--cb-wf-columns", "2");
      rows.push({
        node: sized(row, { height }),
        height,
        least: height - noteSpare,
      });
    }

    return rows;
  }
}

/**
 * How tall a note prints, from how many lines its text takes.
 *
 * A line of the note's column holds about 50 Latin letters at the note's
 * size, and a 汉字 takes the room of two.
 */
export function noteHeight(note: string): number {
  const width = Array.from(note).reduce(
    (sum, letter) => sum + (/\p{Script=Han}/u.test(letter) ? 2 : 1),
    0,
  );
  const lines = Math.max(2, Math.ceil(width / 50));

  return lines * 3.9 + 3;
}
